import React from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import { useSeo } from "@/lib/seo";

export default function PolitiqueConfidentialite() {
  useSeo({
    title: "Politique de confidentialité | My Animation",
    description:
      "Politique de confidentialité de My Animation. Traitement, conservation et protection des données personnelles collectées via le formulaire de devis.",
    path: "/politique-confidentialite",
    keywords: "politique de confidentialité My Animation, RGPD, données personnelles",
  });

  return (
    <>
      <PageHero
        eyebrow="Confidentialité"
        title={<>Politique de <span className="italic gold-text">confidentialité.</span></>}
        subtitle="Vos données personnelles sont traitées avec attention. Cette page détaille comment My Animation collecte, utilise et protège vos informations."
        img="https://images.pexels.com/photos/9665186/pexels-photo-9665186.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
      />

      <section className="py-20 md:py-28 px-6 md:px-10 lg:px-14 max-w-[900px] mx-auto">
        <div className="card-luxe p-8 md:p-12 space-y-10">
          <Block title="Responsable du traitement">
            <P>
              Les données collectées via ce site sont traitées par <strong>My Animation</strong>,
              SIRET 532 387 156 00039, joignable au{" "}
              <a className="text-[#DCB457] hover:underline" href="tel:0769224956">07 69 22 49 56</a> ou par e-mail à{" "}
              <a className="text-[#DCB457] hover:underline" href="mailto:my-animation@outlook.fr">my-animation@outlook.fr</a>.
            </P>
          </Block>

          <Block title="Données collectées">
            <P>
              My Animation collecte uniquement les informations strictement
              nécessaires au traitement de votre demande de devis :
            </P>
            <ul className="list-disc list-inside space-y-1 text-white/70">
              <li>Nom complet</li>
              <li>Adresse e-mail</li>
              <li>Numéro de téléphone</li>
              <li>Type, date, lieu et horaires de l'événement</li>
              <li>Nombre d'invités</li>
              <li>Prestations souhaitées</li>
              <li>Tout message libre que vous communiquez</li>
            </ul>
          </Block>

          <Block title="Finalités du traitement">
            <P>
              Les données collectées via le formulaire de devis sont utilisées
              uniquement pour vous répondre, établir un devis personnalisé et
              assurer la bonne organisation de votre prestation. Elles ne font
              l'objet d'aucune communication commerciale ni d'aucun transfert à
              des tiers.
            </P>
          </Block>

          <Block title="Conservation des données">
            <P>
              Les données de devis sont conservées pour la durée nécessaire au
              traitement de votre demande et à la gestion de la relation client,
              puis archivées dans le respect des obligations légales (notamment
              comptables).
            </P>
          </Block>

          <Block title="Vos droits">
            <P>
              Conformément au RGPD, vous disposez d'un droit d'accès, de
              rectification, d'effacement, de portabilité et d'opposition au
              traitement de vos données personnelles. Pour exercer ces droits,
              il suffit d'écrire à my-animation@outlook.fr.
            </P>
          </Block>

          <Block title="Sécurité">
            <P>
              My Animation met en œuvre les mesures techniques et organisationnelles
              nécessaires pour préserver la confidentialité et la sécurité des
              données collectées.
            </P>
          </Block>

          <Block title="Cookies et mesure d'audience">
            <P>
              Le site utilise des cookies pour trois finalités distinctes, gérées via
              un bandeau de consentement conforme au RGPD et à la recommandation CNIL.
              Aucun cookie de mesure d'audience ni de marketing n'est déposé avant
              votre accord explicite.
            </P>
            <p className="mt-4 font-semibold text-white">Cookies strictement nécessaires (toujours actifs)</p>
            <P>
              Ils permettent le fonctionnement du site : session, préférence de langue
              (<code className="text-[#DCB457]">myanimation.lang</code>), PWA. Ils ne
              collectent aucune donnée d'usage.
            </P>
            <p className="mt-4 font-semibold text-white">Google Analytics 4 (mesure d'audience, opt-in)</p>
            <P>
              Fournisseur : Google Ireland Limited. Identifiant de mesure :{" "}
              <code className="text-[#DCB457]">G-DHKW8QKVPR</code>. Finalités : mesure
              du trafic, des sources d'acquisition, des conversions (demandes de devis,
              clics téléphone / email, téléchargements de brochure). Cookies déposés :{" "}
              <code className="text-[#DCB457]">_ga</code>,{" "}
              <code className="text-[#DCB457]">_ga_DHKW8QKVPR</code>. Durée de conservation
              par défaut : 14 mois (paramétrable dans GA4). L'adresse IP est anonymisée
              avant traitement. Aucune donnée directement identifiable (nom, email,
              téléphone, adresse, contenu de message, numéro de devis) n'est transmise.
            </P>
            <p className="mt-4 font-semibold text-white">PostHog / Analytics Emergent (mesure d'audience, opt-in)</p>
            <P>
              Outil de mesure interne de la plateforme Emergent. Finalités : mesure
              d'usage anonymisée du site. Enregistrement de session <strong>désactivé</strong>.
              Capture automatique des interactions (autocapture) <strong>désactivée</strong>.
              Stockages utilisés : cookie et clés localStorage/sessionStorage préfixées{" "}
              <code className="text-[#DCB457]">ph_*</code>. Durée : 12 mois.
            </P>
            <p className="mt-4 font-semibold text-white">Cookies techniques de l'infrastructure</p>
            <P>
              Deux cookies déposés côté serveur par l'infrastructure d'hébergement
              (<code className="text-[#DCB457]">__emg_vid</code>,{" "}
              <code className="text-[#DCB457]">__emg_sid</code>) apparaissent lors de la
              connexion à notre serveur. Leur finalité exacte est en cours de
              clarification auprès de notre hébergeur ; cette section sera mise à jour
              dès réception de la réponse officielle.
            </P>
            <p className="mt-4 font-semibold text-white">Marketing</p>
            <P>Aucun tracker publicitaire tiers n'est actuellement actif sur le site.</P>
            <p className="mt-4 font-semibold text-white">Vos droits</p>
            <P>
              Vous pouvez à tout moment accepter, refuser ou modifier vos choix via
              le bouton <strong>« Gérer mes cookies »</strong> disponible en pied de page.
              Le retrait du consentement entraîne la suppression immédiate des cookies
              de mesure d'audience (<code className="text-[#DCB457]">_ga*</code>,{" "}
              <code className="text-[#DCB457]">ph_*</code>) déposés sur votre appareil.
              Aucun tracker de mesure d'audience n'est réactivé sans un nouveau
              consentement explicite.
            </P>
          </Block>

          <Block title="Contact">
            <P>
              Pour toute question concernant la présente politique, contactez-nous
              à my-animation@outlook.fr ou consultez nos{" "}
              <Link to="/mentions-legales" className="text-[#DCB457] hover:underline">
                mentions légales
              </Link>.
            </P>
          </Block>
        </div>
      </section>
    </>
  );
}

function Block({ title, children }) {
  return (
    <div>
      <h2 className="font-serif-luxe text-2xl md:text-3xl mb-3">
        <span className="italic gold-text">{title}</span>
      </h2>
      <div className="space-y-2 text-white/70 leading-relaxed text-[15px]">{children}</div>
    </div>
  );
}

function P({ children }) {
  return <p>{children}</p>;
}
